/**
 * Design System Utilities
 *
 * This file provides utilities for consistent design system usage across the application.
 * It includes color mappings, spacing utilities, and component style helpers.
 */

// Design system color mappings from Tailwind config
export const designSystemColors = {
  // Primary colors
  // Polar Night: ice cyan (dark) to deep teal (light); never purple.
  primary: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#c5f6ff",
    300: "#8ff0ff",
    400: "#5ee7ff",
    500: "#0b8ea3",
    600: "#0b6b7d",
    700: "#0a5866",
    800: "#0a4652",
    900: "#08343d",
    950: "#051f25",
  },

  // Secondary colors
  secondary: {
    50: "#f0f9ff",
    100: "#e0f2fe",
    200: "#bae6fd",
    300: "#7dd3fc",
    400: "#38bdf8",
    500: "#0ea5e9",
    600: "#0284c7",
    700: "#0369a1",
    800: "#075985",
    900: "#0c4a6e",
    950: "#082f49",
  },

  // Success colors
  success: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },

  // Warning colors
  warning: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
    950: "#451a03",
  },

  // Danger colors
  danger: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
  },

  // Neutral colors
  gray: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#020617",
  },
};

// Chart color palettes using design system colors
export const chartColorPalettes = {
  default: [
    {
      light: `rgba(11, 142, 163, 0.7)`, // primary-500 with opacity
      dark: `rgba(94, 231, 255, 0.7)`, // primary-400 with opacity
      border: designSystemColors.primary[500],
      solid: designSystemColors.primary[500],
    },
    {
      light: `rgba(14, 165, 233, 0.7)`, // secondary-500 with opacity
      dark: `rgba(56, 189, 248, 0.7)`, // secondary-400 with opacity
      border: designSystemColors.secondary[500],
      solid: designSystemColors.secondary[500],
    },
    {
      light: `rgba(34, 197, 94, 0.7)`, // success-500 with opacity
      dark: `rgba(74, 222, 128, 0.7)`, // success-400 with opacity
      border: designSystemColors.success[500],
      solid: designSystemColors.success[500],
    },
    {
      light: `rgba(245, 158, 11, 0.7)`, // warning-500 with opacity
      dark: `rgba(251, 191, 36, 0.7)`, // warning-400 with opacity
      border: designSystemColors.warning[500],
      solid: designSystemColors.warning[500],
    },
    {
      light: `rgba(239, 68, 68, 0.7)`, // danger-500 with opacity
      dark: `rgba(248, 113, 113, 0.7)`, // danger-400 with opacity
      border: designSystemColors.danger[500],
      solid: designSystemColors.danger[500],
    },
  ],

  success: [
    {
      light: `rgba(34, 197, 94, 0.7)`,
      dark: `rgba(74, 222, 128, 0.7)`,
      border: designSystemColors.success[500],
      solid: designSystemColors.success[500],
    },
    {
      light: `rgba(16, 185, 129, 0.7)`,
      dark: `rgba(52, 211, 153, 0.7)`,
      border: designSystemColors.success[600],
      solid: designSystemColors.success[600],
    },
  ],

  danger: [
    {
      light: `rgba(239, 68, 68, 0.7)`,
      dark: `rgba(248, 113, 113, 0.7)`,
      border: designSystemColors.danger[500],
      solid: designSystemColors.danger[500],
    },
    {
      light: `rgba(220, 38, 38, 0.7)`,
      dark: `rgba(239, 68, 68, 0.7)`,
      border: designSystemColors.danger[600],
      solid: designSystemColors.danger[600],
    },
  ],

  info: [
    {
      light: `rgba(14, 165, 233, 0.7)`,
      dark: `rgba(56, 189, 248, 0.7)`,
      border: designSystemColors.secondary[500],
      solid: designSystemColors.secondary[500],
    },
    {
      light: `rgba(2, 132, 199, 0.7)`,
      dark: `rgba(14, 165, 233, 0.7)`,
      border: designSystemColors.secondary[600],
      solid: designSystemColors.secondary[600],
    },
  ],
};

// Theme-aware color utilities
export const getThemeColors = (isDarkMode) => ({
  text: {
    primary: isDarkMode
      ? designSystemColors.gray[100]
      : designSystemColors.gray[900],
    secondary: isDarkMode
      ? designSystemColors.gray[400]
      : designSystemColors.gray[600],
    muted: isDarkMode
      ? designSystemColors.gray[500]
      : designSystemColors.gray[500],
  },
  background: {
    primary: isDarkMode
      ? designSystemColors.gray[900]
      : designSystemColors.gray[50],
    secondary: isDarkMode
      ? designSystemColors.gray[800]
      : designSystemColors.gray[100],
    card: isDarkMode ? designSystemColors.gray[800] : "#ffffff",
  },
  border: {
    primary: isDarkMode
      ? designSystemColors.gray[700]
      : designSystemColors.gray[200],
    secondary: isDarkMode
      ? designSystemColors.gray[600]
      : designSystemColors.gray[300],
  },
  grid: isDarkMode ? "rgba(226, 232, 240, 0.1)" : "rgba(100, 116, 139, 0.1)",
  tooltip: {
    background: isDarkMode ? designSystemColors.gray[800] : "#ffffff",
    border: isDarkMode
      ? designSystemColors.gray[700]
      : designSystemColors.gray[200],
  },
});

// Chart theme configuration
export const getChartTheme = (isDarkMode) => {
  const themeColors = getThemeColors(isDarkMode);

  return {
    textColor: themeColors.text.primary,
    gridColor: themeColors.grid,
    tooltipBackground: themeColors.tooltip.background,
    tooltipBorder: themeColors.tooltip.border,
    backgroundColor: themeColors.background.primary,
  };
};

// Utility function to get design system color
export const getDesignSystemColor = (colorName, shade = 500, opacity = 1) => {
  const color = designSystemColors[colorName]?.[shade];
  if (!color) {
    console.warn(`Design system color not found: ${colorName}-${shade}`);
    return designSystemColors.gray[500]; // fallback
  }

  if (opacity === 1) {
    return color;
  }

  // Convert hex to rgba with opacity
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Component style utilities
export const getComponentStyles = (isDarkMode) => ({
  card: {
    background: isDarkMode ? "bg-gray-800" : "bg-white",
    border: isDarkMode ? "border-gray-700" : "border-gray-200",
    text: isDarkMode ? "text-gray-100" : "text-gray-900",
  },

  button: {
    primary: {
      background: "bg-primary-600 hover:bg-primary-700",
      text: "text-white",
      border: "border-primary-600",
    },
    secondary: {
      background: isDarkMode
        ? "bg-gray-700 hover:bg-gray-600"
        : "bg-gray-100 hover:bg-gray-200",
      text: isDarkMode ? "text-gray-100" : "text-gray-900",
      border: isDarkMode ? "border-gray-600" : "border-gray-300",
    },
  },

  input: {
    background: isDarkMode ? "bg-gray-800" : "bg-white",
    border: isDarkMode ? "border-gray-600" : "border-gray-300",
    text: isDarkMode ? "text-gray-100" : "text-gray-900",
    placeholder: isDarkMode ? "placeholder-gray-400" : "placeholder-gray-500",
  },
});

// Spacing utilities based on design system
export const spacing = {
  xs: "0.25rem", // 1
  sm: "0.5rem", // 2
  md: "1rem", // 4
  lg: "1.5rem", // 6
  xl: "2rem", // 8
  "2xl": "3rem", // 12
  "3xl": "4rem", // 16
  "4xl": "6rem", // 24
};

// Typography utilities
export const typography = {
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
  },

  fontWeight: {
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
  },

  lineHeight: {
    tight: "1.25",
    normal: "1.5",
    relaxed: "1.75",
  },
};

export default {
  designSystemColors,
  chartColorPalettes,
  getThemeColors,
  getChartTheme,
  getDesignSystemColor,
  getComponentStyles,
  spacing,
  typography,
};
